<template>
    <v-container fluid fill-height class="pa-0 flex-column" v-if="!pageLoading">
        <v-card width="100%" tile color="grey lighten-4" elevation="0" class="py-8 py-lg-12">
            <v-row justify="center">
                <v-col cols="12" lg="6" class="text-center">
                    <h1 class="display-1 my-1">{{ $t('RegistrationProfileStepPageTitle') }}</h1>
                    <p class="body-1 mt-4">{{ $t('RegistrationProfileStepPageDescription') }}</p>
                </v-col>
            </v-row>
        </v-card>   
        <v-container class="py-0">

            <v-card tile color="primary" width="100%" dark class="position-fixed">
                <v-tabs
                    v-model="tab"
                    fixed-tabs
                    background-color="primary"
                    dark
                    show-arrows
                    icons-and-text
                >
                    <v-tabs-slider color="primary lighten-4"></v-tabs-slider>
                    <v-tab 
                        v-for="(tab, index) in tabs" 
                        :key="index" 
                        :href="`#tab-${index}`"
                        :disabled="index !== 0 && !tabs[index-1].completed && !isSubscribed"
                    >
                        {{ tab.text }}
                        <v-icon v-if="tab.completed">mdi-check</v-icon>
                        <div class="title" v-text="index+1" v-else></div>
                    </v-tab>
                </v-tabs>
            </v-card>
            <template v-if="isSubscribed">
                <v-row justify="center">
                    <v-col cols="12" md="8" lg="6">
                        <v-alert
                            max-width="650"
                            type="success"
                            prominent
                            class="mt-6 mx-auto"
                        >
                            <div class="d-flex align-center justify-space-between">
                                <span class="font-weight-medium">{{ $t('SuccessfulRegistrationNotification') }}</span> 
                                <v-btn outlined rounded to="/en/student/tutors">{{ $t('MentorPageRedirectLink') }}</v-btn>
                            </div>
                        </v-alert>
                    </v-col>
                </v-row>
            </template>
            <template v-else>
                <v-tabs v-model="tab">
                    <v-tab-item value="tab-1">
                        <div class="title text-center" v-if="type === 'parent'" v-text="$t('ChildProfileFormTitle')"></div>

                        <v-row justify="center"  class="pa-lg-4" v-if="!profileLoading">
                            <v-col cols="12" md="8" lg="6">
                                <v-row>

                                    <v-col cols="12">
                                        <ValidationObserver v-slot="{ handleSubmit }">
                                            <form @submit.prevent="handleSubmit(updateProfile)">
                                                <template v-if="type === 'parent'">
                                                    <ValidationProvider name="Child first name" rules="required" v-slot="{ errors }">
                                                        <v-text-field
                                                            rounded
                                                            outlined
                                                            persistent-hint
                                                            validate-on-blur
                                                            hint="Required"
                                                            :label="$t('ChildFirstName')"
                                                            v-model="student.studentFirstName"
                                                            class="mb-2"
                                                            :error-messages="errors[0]"
                                                        ></v-text-field>
                                                    </ValidationProvider>
                                                    <ValidationProvider name="Child last name" rules="required" v-slot="{ errors }">
                                                        <v-text-field
                                                            rounded
                                                            outlined
                                                            persistent-hint
                                                            validate-on-blur
                                                            hint="Required"
                                                            :label="$t('ChildLastName')"
                                                            v-model="student.studentLastName"
                                                            class="mb-2"
                                                            :error-messages="errors[0]"
                                                        ></v-text-field>
                                                    </ValidationProvider>
                                                </template>
                                                <v-file-input 
                                                    rounded
                                                    outlined
                                                    :label="$t('UploadProfilePicture')"
                                                    prepend-icon=""
                                                    prepend-inner-icon="mdi-image"
                                                    @change="profilePictureChangeEventHandler"
                                                ></v-file-input>
                                                <v-text-field
                                                    rounded
                                                    outlined
                                                    v-model="student.student.phone"
                                                    :label="$t('PhoneNumber')"
                                                ></v-text-field>
                                                <ValidationProvider name="Country" rules="positive" v-slot="{ errors }">
                                                    <v-select
                                                        rounded
                                                        outlined
                                                        persistent-hint
                                                        validate-on-blur
                                                        hint="Required"
                                                        v-model="student.student.countryId"
                                                        :items="countries"
                                                        item-text="label"
                                                        item-value="id"
                                                        :label="$t('Country')"
                                                        class="mb-2"
                                                        :menu-props="{ offsetY: true }"
                                                        :error-messages="errors[0]"
                                                    ></v-select>
                                                </ValidationProvider>
                                                <div class="d-flex align-center justify-space-between mb-4">
                                                    <p class="subtitle-1 pb-0 mb-0" v-text="$t('FormSubjectsOfInterest')">:</p>
                                                    <v-btn 
                                                        small
                                                        rounded 
                                                        depressed
                                                        color="primary" 
                                                        @click="subjectsDialog = true"
                                                    ><v-icon left>mdi-plus</v-icon> {{ $t('FormAddSubjects') }}</v-btn>
                                                </div>
                                                <ValidationProvider name="Academic level" rules="positive" v-slot="{ errors }">                                     
                                                    <v-select
                                                        rounded
                                                        outlined
                                                        persistent-hint
                                                        validate-on-blur
                                                        hint="Required"
                                                        v-model="student.academicLevelId"
                                                        :items="academicLevels"
                                                        item-text="label"
                                                        item-value="id"
                                                        :label="$t('AcademicLevel')"
                                                        class="mt-6 mb-2"
                                                        :menu-props="{ offsetY: true }"
                                                        :error-messages="errors[0]"
                                                    ></v-select>
                                                </ValidationProvider>
                                                <template v-if="student.academicLevelId !== 24">
                                                    <ValidationProvider name="School name" rules="required" v-slot="{ errors }">   
                                                        <v-text-field
                                                            rounded
                                                            outlined
                                                            persistent-hint
                                                            validate-on-blur
                                                            hint="Required"
                                                            v-model="student.schoolName"
                                                            :label="$t('SchoolName')"
                                                            class="mb-2"
                                                            :error-messages="errors[0]"
                                                        ></v-text-field>
                                                    </ValidationProvider>
                                                    <ValidationProvider name="School country" rules="required" v-slot="{ errors }">   
                                                        <v-select
                                                            rounded
                                                            outlined
                                                            persistent-hint
                                                            hint="Required"
                                                            v-model="student.schoolCountryId"
                                                            :items="countries"
                                                            item-text="label"
                                                            item-value="id"
                                                            :label="$t('SchoolCountry')"
                                                            class="mb-2"
                                                            :menu-props="{ offsetY: true }"
                                                            :error-messages="errors[0]"
                                                            @change="fetchStatesByCountry"
                                                        ></v-select>
                                                    </ValidationProvider>
                                                    <ValidationProvider 
                                                        name="School state" 
                                                        rules="positive" 
                                                        v-slot="{ errors }" 
                                                        v-if="states.length > 0"
                                                    >   
                                                        <v-select
                                                            rounded
                                                            outlined
                                                            persistent-hint
                                                            hint="Required"
                                                            v-model="student.schoolStateId"
                                                            :items="states"
                                                            item-text="label"
                                                            item-value="id"
                                                            :label="$t('SchoolState')"
                                                            class="mb-2"
                                                            :menu-props="{ offsetY: true }"
                                                            :error-messages="errors[0]"
                                                        ></v-select>                        
                                                    </ValidationProvider>
                                                    <v-text-field
                                                        rounded
                                                        outlined
                                                        v-model="student.student.address"
                                                        :label="$t('Address')"
                                                        class="mb-2"
                                                    ></v-text-field>
                                                </template>
                                                <div class="d-flex mt-4">
                                                    <v-btn
                                                        rounded 
                                                        depressed 
                                                        color="primary"
                                                        type="submit"
                                                        class="mx-auto"
                                                        :disabled="profileUpdating"
                                                        :loading="profileUpdating"
                                                    >
                                                        {{ $t('Save&Continue') }}
                                                        <!-- <template v-if="tabs[0].completed">{{ $t('Update') }}</template>
                                                        <template v-else>{{ $t('Save') }}</template> -->
                                                    </v-btn>
                                                    <!-- <v-spacer></v-spacer>
                                                    <v-btn 
                                                        rounded 
                                                        depressed 
                                                        color="primary"
                                                        :disabled="!tabs[0].completed"
                                                        @click="nextTabEventHandler"
                                                        v-text="$t('NextStep')"
                                                    ></v-btn> -->
                                                </div>
                                            </form>
                                        </ValidationObserver>                                        
                                    </v-col>
                                </v-row>
                            </v-col>                            
                        </v-row>
                    </v-tab-item>            
                    <v-tab-item value="tab-0">
                        <v-row justify="center">
                            <v-col cols="12" md="8" lg="5">
                                <v-row>
                                    <v-col>
                                        <v-text-field
                                            rounded
                                            outlined
                                            required
                                            persistent-hint
                                            hint="Required"
                                            color="primary"
                                            :label="$t('BillingAddress')"
                                            class="mb-2"
                                            v-model="billingAddress.text"
                                            :rules="billingAddressRules.text"
                                        ></v-text-field>
                                        <v-row>
                                            <v-col cols="12" :md="billingStates.length > 0 ? 6 : 12" class="py-0 mb-2">
                                                <v-select
                                                    rounded
                                                    outlined
                                                    required
                                                    persistent-hint
                                                    hint="Required"
                                                    :rules="billingAddressRules.country"
                                                    v-model="billingAddress.country"
                                                    :items="countries"
                                                    item-text="label"
                                                    item-value="id"
                                                    color="primary"
                                                    :label="$t('Country')"
                                                    @change="fetchStatesByCountry"
                                                ></v-select>
                                            </v-col>
                                            <v-col cols="12" md="6" class="py-0" v-if="billingStates.length > 0">
                                                <v-select
                                                    rounded
                                                    outlined
                                                    required
                                                    hint="Required"
                                                    persistent-hint
                                                    :rules="billingAddressRules.state"
                                                    v-model="billingAddress.state"
                                                    :items="billingStates"
                                                    item-text="label"
                                                    item-value="label"
                                                    color="primary"
                                                    :label="$t('State')"
                                                ></v-select>
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12" md="6" class="py-0 mb-2">
                                                <v-text-field
                                                    rounded
                                                    outlined
                                                    required
                                                    hint="Required"
                                                    persistent-hint
                                                    v-model="billingAddress.code"
                                                    :rules="billingAddressRules.code"
                                                    color="primary"
                                                    :label="$t('ZipCode')"
                                                ></v-text-field>
                                            </v-col>
                                            <v-col cols="12" md="6" class="py-0">
                                                <v-text-field
                                                    rounded
                                                    outlined
                                                    required
                                                    hint="Required"
                                                    persistent-hint
                                                    v-model="billingAddress.city"
                                                    :rules="billingAddressRules.city"
                                                    color="primary"
                                                    :label="$t('City')"
                                                ></v-text-field>
                                            </v-col>
                                        </v-row>
                                         <v-text-field
                                            rounded
                                            outlined
                                            required
                                            hint="Required"
                                            persistent-hint
                                            color="primary"
                                            v-model="card.name"
                                            :rules="cardRules.name"
                                            :label="$t('CardName')"
                                            class="mb-2"
                                        ></v-text-field>
                                        <!-- <v-text-field
                                            rounded
                                            outlined
                                            required
                                            hint="Required"
                                            persistent-hint
                                            color="primary"
                                            v-mask="'#### #### #### ####'"
                                            v-model="card.number"
                                            :rules="cardRules.number"
                                            prepend-inner-icon="mdi-credit-card"
                                            :label="$t('CardNumber')"
                                            class="mb-2"
                                        ></v-text-field> -->
                                        <v-row>
                                            <!-- <v-col cols="12" md="8" class="py-0 mb-2">
                                                <v-text-field
                                                    rounded
                                                    outlined
                                                    required
                                                    hint="Required"
                                                    persistent-hint
                                                    v-model="card.expiration"
                                                    :rules="cardRules.expiration"
                                                    v-mask="'##/####'"
                                                    suffix="MM/YYYY"
                                                    color="primary"
                                                    :label="$t('Expiration')"
                                                ></v-text-field>
                                            </v-col>
                                            <v-col cols="12" md="4" class="py-0">
                                                <v-text-field
                                                    rounded
                                                    outlined
                                                    requirrequired
                                                    hint="Required"
                                                    persistent-hint
                                                    v-model="card.cvc"
                                                    :rules="cardRules.cvc"
                                                    color="primary"
                                                    :label="$t('CVC')"
                                                ></v-text-field>
                                            </v-col> -->
                                            <v-col cols="12">
                                                <v-row>
                                                    <div v-if="tab == 'tab-0'" ref="cardElement" style="background:#fff;
                                                        background: rgb(255, 255, 255);
                                                        width: 100%;
                                                        margin: 0 auto;
                                                        height: 30px;border:1px solid;border-radius:30px;padding:20px;padding:20px"></div>
                                                  </v-row>
                                            </v-col>
                                        </v-row>   
                                                                     
                                        <div class="d-flex mt-4">                                    
                                            <v-btn 
                                                rounded
                                                depressed
                                                color="primary"
                                                class="mx-auto"
                                                :disabled="!isPaymentMethodFormValid"
                                                :loading="paymenthMethodRequsting"
                                                @click="registerPaymentMethod"
                                                v-if="!card.compilation"
                                            >{{ $t('Save&Continue') }}</v-btn>
                                            <!-- <v-spacer></v-spacer>
                                            <v-btn 
                                                rounded
                                                depressed
                                                color="primary"
                                                :disabled="!this.card.compilation"
                                                @click="nextTabEventHandler"
                                            >{{ $t('NextStep') }}</v-btn> -->
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>                
                    </v-tab-item>
                    <!-- <div>
                        <div ref="cardElement" style="width:400px"></div>
                      </div> -->
                    <v-tab-item value="tab-2">
                        <v-row justify="center" v-if="!subscriptionPackagesLoading">
                            <v-col cols="12" lg="6">
                                <v-row>
                                    <v-col 
                                        v-for="(plan, index) in subscriptionPackages"
                                        :key="index"
                                        cols="12" 
                                        md="6"
                                        class="d-flex"
                                    >
                                        <button 
                                            type="button" 
                                            class="subscription-package-card-btn" 
                                            :class="selectedSubscriptionPackage.id == plan.id ? 'active' : ''"
                                            @click="selectedSubscriptionPackageEventHandler(plan)"
                                        >
                                            <div class="card-body">
                                                <v-chip small outlined color="primary" class="mb-6" v-if="index === 0" v-text="$t('RecommendedPackage')"></v-chip>
                                                <div class="headline primary--text" v-text="plan.label"></div>
                                                <div class="title font-weight-bold mb-4 primary--text" v-text="`${plan.price} (${plan.currency})`"></div>
                                                <div class="body-1 grey--text lighten-4" v-text="plan.description"></div>
                                            </div>
                                        </button>
                                    </v-col>
                                    <v-col cols="12 mt-2" class="text-center">
                                        <v-btn 
                                            rounded 
                                            depressed 
                                            @click="nextTabEventHandler" 
                                            :disabled="selectedSubscriptionPackage.id == 0" 
                                            color="primary" 
                                            class="ml-auto"
                                            v-text="$t('NextStep')"
                                        ></v-btn>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-tab-item>
                    <v-tab-item value="tab-3">
                        <v-row justify="center">
                            <v-col cols="12" lg="6">
                                <v-card outlined class="custom-radius">
                                    <div class="headline pa-4" v-text="$t('FinishRegistrationProfileSteps')"></div>
                                    <v-divider></v-divider>
                                    <v-list class="pt-0">
                                        <v-list-item>
                                            <v-list-item-content>
                                                <div class="title font-weight-medium" v-text="`${user.firstName} ${user.lastName}`"></div>
                                                <v-list-item-title class=" font-weight-regular" v-text="`${$t('BillingAddress')}: ${billingAddress.text}`"></v-list-item-title>
                                                <v-list-item-title class=" font-weight-regular" v-if="billingAddress.country !== ''" v-text="`${$t('Country')}: ${getCountryById(billingAddress.country)}`"></v-list-item-title>
                                                <v-list-item-title class=" font-weight-regular" v-text="`${$t('State')}: ${billingAddress.state}`"></v-list-item-title>
                                                <v-list-item-title class=" font-weight-regular" v-text="`${$t('ZipCode')}: ${billingAddress.code}`"></v-list-item-title>
                                                <v-list-item-title class=" font-weight-regular" v-text="`${$t('City')}: ${billingAddress.city}`"></v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider></v-divider>                                   
                                        <v-list-item>
                                            <v-list-item-content>
                                                <v-list-item-title v-text="`Subscription package: ${selectedSubscriptionPackage.name}`"></v-list-item-title>
                                                <v-list-item-title v-text="`Price: ${selectedSubscriptionPackage.price} (${selectedSubscriptionPackage.currency})`"></v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-list>
                                </v-card>
                                <div class="text-center">
                                    <v-btn 
                                        rounded 
                                        depressed 
                                        color="primary" 
                                        class="my-4 my-lg-6" 
                                        :disabled="subscribeRequest"
                                        :loading="subscribeRequest"
                                        @click="confirmSubscription"
                                    >{{ $t('Save') }}</v-btn>
                                </div>
                            </v-col>
                        </v-row>
                    </v-tab-item>
                </v-tabs> 
            </template>
        </v-container>
        <v-dialog v-model="subjectsDialog" scrollable max-width="600px">
            <v-card>
                <v-card-title v-text="$t('FormSubjectsOfInterest')"></v-card-title>
                <v-divider></v-divider>
                <v-card-text color="white" class="px-2 py-0" max-height="90vh">
                    <v-card 
                        color="grey lighten-4"
                        class="my-2"
                        flat
                        v-for="(category, index) in categories"
                        :key="index"
                    >
                        <template v-if="category.teachingSubjects.length !== 0">
                            <div class="overline pl-4 pt-4" v-text="category.teachingCategory.label"></div>
                            <v-card-text class="pt-0">
                                <div class="d-inline-block pr-2"
                                    v-for="(subject, subjectIndex) in category.teachingSubjects"
                                    :key="subjectIndex">
                                    <v-checkbox
                                        hide-details
                                        class="mt-1"
                                        v-model="subject.present"
                                        :label="subject.label"
                                        :id="`subject-${subject.id}`"
                                    ></v-checkbox>
                                </div>
                            </v-card-text>
                        </template>
                    </v-card>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-btn 
                        small
                        rounded 
                        depressed 
                        color="primary" 
                        @click="subjectsDialog = false" 
                        class="mx-auto" 
                        v-text="$t('Save')"
                    ></v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        small
                        rounded 
                        depressed 
                        v-text="$t('Close')"
                        @click="subjectsDialog = false"
                    ></v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
    import { mapGetters } from 'vuex'
    import { mask } from 'vue-the-mask'
    import { ValidationObserver, ValidationProvider, extend } from 'vee-validate'
    import { required } from 'vee-validate/dist/rules'
    import { loadStripe } from '@stripe/stripe-js'


    extend('positive', value => {
        return value > 0
    })

    extend('required', {
        ...required,
        message: 'This field is required'
    })

    export default {
        name: 'subscription',
        data () {
            const defaultbillingAddressForm = Object.freeze({
                text: '',
                country: '',
                city: '',
                state: '',
                code: '',
                compilation: false
            })
            const defaultCard = Object.freeze({
                name: '',
                number: '',
                expiration: null,
                expireYear: null,
                expireMonth: null,
                cvc: '',
                compilation: false,
                isValid: false
            })
            return {
                // Tabs data
                stripeCard: null, 
                stripe: null,
                tab: "tab-0",
                tabs: [
                    {
                        text: this.$i18n.t('RegistrationProfileStepTwo'),
                        completed: false
                    },
                    {
                        text: this.$i18n.t('RegistrationProfileStepOne'),
                        completed: false
                    },
                    {
                        text: this.$i18n.t('RegistrationProfileStepThree'),
                        completed: false
                    },                
                    {
                        text: this.$i18n.t('RegistrationProfileStepFour'),
                        completed: false
                    }
                ],
                // Student data
                student: {},
                countries: [],
                categories: [],
                academicLevels: [],
                states: [],
                billingStates: [],
                profilePicture: {
                    base64String: '',
                    fileName: '',
                    fileExtension: ''
                },
                subjectsDialog: false,
                profileLoading: true,
                profileUpdating: false,
                // Billing address data
                billingAddress: Object.assign({}, defaultbillingAddressForm),
                billingAddressRules: {
                    text: [val => (val || '').length > 0 || 'This field is required'],
                    country: [val => val > 0 || 'This field is required'],
                    city: [val => (val || '').length > 0 || 'This field is required'],
                    state: [val => (val || '').length > 0 || 'This field is required'],
                    code: [val => (val || '').length > 0 || 'This field is required'],
                },
                // Subscription plan data
                selectedSubscriptionPackage: {
                    id: 0
                },
                subscriptionPackages: [],
                card: Object.assign({}, defaultCard),
                cardRules: {
                    name: [val => (val || '').length > 0 || 'This field is required'],
                    number: [val => (val || '').length > 0 || 'This field is required'],
                    expiration: [val => (val || '').length > 0 || 'This field is required'],
                    cvc: [val => (val || '').length > 0 || 'This field is required'],
                },
                paymenthMethodRequsting: false,
                subscriptionPackagesLoading: false,
                subscribeRequest: false,
                subscribedMessage: '',
                pageLoading: false,
                tokenId:null
            }
        },
        directives: {
            mask
        },
        components: {
            ValidationObserver,
            ValidationProvider
        },
        computed: {
            ...mapGetters(['user', 'type', 'isSubscribed', 'preferedLanguage']),
            isPaymentMethodFormValid() {
                return (
                    this.billingAddress.text && 
                    this.billingAddress.country && 
                    this.billingAddress.city && 
                    this.billingAddress.code 
                )
            }      
        },
        mounted() {
            this.initStripe()
            this.fetchStudentProfile()
            this.fetchSubscriptionPackages()
        },
        methods: {
            // Profile methods
            async initStripe() {
                debugger
                this.stripe = await loadStripe(`pk_live_51HRJ9GLvKi1GZConf7Hx36VrMhWCIQdQRYV9JglQpTtnAEPwGVuQ7eE3Isg1XQ4MSuXgMZ4Z7eBNSA1ly0HtY29L00uBYUo4Wl`)
                const elements = this.stripe.elements();
                    // Create an instance of the card Element
                    const cardElement = elements.create('card');
                        console.log(this.$refs.cardElement);
                    // Mount the card Element to the cardElement div
                    cardElement.mount(this.$refs.cardElement);

               // Ensure that the ref exists and is a valid DOM element
    if (this.$refs.cardElement instanceof HTMLElement) {
      // Mount the card Element to the cardElement div
      cardElement.mount(this.$refs.cardElement);
      this.stripeCard = cardElement;
    } else {
      console.error('Invalid or missing DOM element for cardElement ref.');
    }
                    // Now, you can use the cardElement to create a token

                    this.stripeCard = cardElement

                },
               
            async fetchStudentProfile() {
                await this.axios.get('/Student/GetStudentProfile', {
                    params: {
                        userId: this.user.id,
                        languageId: this.preferedLanguage
                    }
                })
                .then(response => {
                    this.student = response.data.userProfile
                    this.categories = response.data.userTeachingSubjects
                    this.countries = response.data.countries
                    this.academicLevels = response.data.academicLevels
                    this.states = response.data.states
                    this.billingStates = response.data.states
                    this.checkIfProfileCompleted()
                    if(this.student.schoolCountryId) {
                        this.fetchStatesByCountry()
                    }
                    if(this.student.schoolStateId == null) {
                        this.student.schoolStateId = 0
                    }
                    if(this.student.schoolCountryId == null) {
                        this.student.schoolCountryId = 0
                    }
                })
                .catch(error => {
                    const snackbar = {
                        show: true,
                        message: error.response.data
                    }
                    this.$EventBus.$emit('snackbar', snackbar)
                })
                this.profileLoading = false
            },
            updateProfile() {
                this.profileUpdating = true
                let selectedSubjects = []
                this.categories.forEach(category => {
                    category.teachingSubjects.forEach(subject => {
                        if(subject.present) {
                            const selectedObject = {
                                teachingSubjectId: subject.id
                            }
                            selectedSubjects.push(selectedObject)
                        }
                    })
                })
                const data = {
                    userId: this.student.student.id,
                    languageId: this.preferedLanguage,
                    profilePicture: this.profilePicture.base64String !== '' ? this.profilePicture : {},
                    phone: this.student.student.phone,
                    countryId: this.student.student.countryId,
                    academicLevelId: this.student.academicLevelId,
                    schoolName: this.student.schoolName,
                    schoolCountryId: this.student.schoolCountryId ? this.student.schoolCountryId : null,
                    schoolStateId: this.student.schoolStateId !== 0 ? this.student.schoolStateId : null,
                    studentTeachingSubject: selectedSubjects,
                    address: this.student.student.address
                }
                if(this.type === 'parent') {
                    data.parentId = this.student.student.id
                    data.studentFirstName = this.student.studentFirstName
                    data.studentLastName = this.student.studentLastName
                }
                this.axios.post('/Student/SaveStudentProfile', data)
                .then(response => {
                    this.student = response.data.userProfile
                    this.tabs[0].completed = true
                    this.profileUpdating = false
                    this.nextTabEventHandler()
                })
                .catch(error => {
                    const snackbar = {
                        show: true,
                        message: error.response.data
                    }
                    this.$EventBus.$emit('snackbar', snackbar)
                    this.profileUpdating = false
                })
            },
            profilePictureChangeEventHandler(event) {
                const file = event
                this.profilePicture.fileName = file.name.split('.')[0]
                this.profilePicture.fileExtension = file.name.split('.')[1]

                var reader = new FileReader()
                reader.readAsDataURL(file)
                reader.onload = () => {
                    this.profilePicture.base64String = reader.result.replace(/^data:.+;base64,/, '')
                    this.previewBase64Image = reader.result
                }
            },
            checkIfProfileCompleted() {
                if(this.student.student.countryId > 0 && this.student.academicLevelId > 0) {
                    this.tabs[0].completed = true
                }
            },
            // Subscription plan methods
            async fetchSubscriptionPackages() {
                this.subscriptionLoading = true
                await this.axios.get('/SubscriptionPlan/GetAll',{
                    params: {
                        languageId: this.preferedLanguage
                    }
                })
                .then(response => {
                    this.subscriptionPackages = response.data.list
                    this.subscriptionLoading = false
                })
                .catch(error => {
                    const snackbar = {
                        show: true,
                        message: error.response.data
                    }
                    this.$EventBus.$emit('snackbar', snackbar)
                    this.subscriptionLoading = false
                })
            },
            async registerPaymentMethod() {
                const {token} = await this.stripe.createToken(this.stripeCard);
                debugger
                if (token && token.id) {
                    debugger;
                    console.log("Token ID:", token);
                    this.tokenId = token.id
                    this.card.cvc = "355"
                    this.card.number = "4242 4242 4242 4242"
                    this.card.expiration = `${token.card.exp_month} + ${token.card.exp_year}`
                    // Perform further actions with the token ID
                    } else {
                    console.error('Token or token ID is missing.');
                    }
                this.paymenthMethodRequsting = true
                const data = {
                    billingAddress: this.billingAddress.text,
                    billingCountry: this.billingAddress.country,
                    billingState: this.billingAddress.state,
                    billingPostal: this.billingAddress.code,
                    billingCity: this.billingAddress.city,
                    number: "4242 4242 4242 4242",
                    expMonth: token.card.exp_month,
                    expYear: token.card.exp_year,
                    name: this.card.name,
                    cvc: "355",
                    paymentMethodId: this.tokenId
                }
                this.axios.post('/Student/RegisterPaymentMethod', data)
                .then(response => {
                    if(response.status === 200) {
                        this.card.compilation = true
                        this.tabs[1].completed = true
                        this.nextTabEventHandler()
                    }
                    this.paymenthMethodRequsting = false
                })
                .catch(error => {
                    const snackbar = {
                        show: true,
                        message: error.response.data
                    }
                    this.$EventBus.$emit('snackbar', snackbar)
                    this.paymenthMethodRequsting = false
                })
            },
            selectedSubscriptionPackageEventHandler(subscriptionPackage) {
                this.selectedSubscriptionPackage.id = subscriptionPackage.id
                this.selectedSubscriptionPackage.name = subscriptionPackage.name
                this.selectedSubscriptionPackage.price = subscriptionPackage.price
                this.selectedSubscriptionPackage.currency = subscriptionPackage.currency
                this.tabs[2].completed = true                
            },
            confirmSubscription() {
                this.subscribeRequest = true
                this.axios.post(`/Student/Subscribe?planId=${this.selectedSubscriptionPackage.id}&languageId=${this.preferedLanguage}`)
                .then(response => {
                    if(response.status === 200) {
                        this.message = response.data.message
                        this.$store.dispatch('subscribe', true)
                    }
                    this.subscribeRequest = false
                })
                .catch(error => {
                    const snackbar = {
                        show: true,
                        message: error.response.data
                    }
                    this.$EventBus.$emit('snackbar', snackbar)
                    this.subscribeRequest = false
                })
            },
            nextTabEventHandler() {
                let currentPosition = parseInt(this.tab.split('-')[1])
                this.tab = `tab-${currentPosition+1}`
            },
            getCountryById(id) {
                let index = this.countries.map(country => country.id).indexOf(id)
                return this.countries[index].label
            },
            fetchStatesByCountry() {
                if(this.tab === 'tab-1') {
                    this.axios.get('/State/GetStatesByCountry', {
                        params: {
                            languageId: this.user.preferedLanguage,
                            countryId: this.student.schoolCountryId
                        }
                    })
                    .then(response => {
                        this.states = response.data.states
                    })
                } else if(this.tab === 'tab-0') {
                    this.axios.get('/State/GetStatesByCountry', {
                        params: {
                            languageId: this.user.preferedLanguage,
                            countryId: this.billingAddress.country
                        }
                    })
                    .then(response => {
                        this.billingStates = response.data.states
                    })
                }
            }
        },
        watch: {
            card: {
                deep: true,
                handler() {
                    if(this.card.expiration) {
                        let month = this.card.expiration.split('/')[0]
                        let year = this.card.expiration.split('/')[1]
                        this.card.expireMonth = month
                        this.card.expireYear = year
                    }
                    if(this.card.number && this.card.expiration && this.card.cvc) {
                        this.card.isValid = true                        
                    } else {
                        this.card.isValid = false
                    }
                }
            },
            tab(newValue,oldValue){
                if(newValue == "tab-0")
                this.initStripe()
            }
        }
    }
</script>

<style lang="scss" scoped>
.subscription-package-card-btn{
    width: 100%;
    height: 100%;
    padding-top: 35px;    
    border: 2px solid $primary;
    border-radius: 15px;
    overflow: hidden;
    @include transition(all .2s ease);
    &:hover{
        @include transform(translateY(-2px));
    }
    &.active{
        background-color: $primary;    
        @include shadow(0px 0px 15px 0px rgba(0,0,0,0.15));
    }
    .card-body{
        width: 100%;
        height: 100%;
        padding: 25px 35px 40px;
        background-color: #fff;
    }    
}
</style>